import { useState, useRef, useEffect, useMemo } from 'react';
import { Building2, MapPin, Warehouse, Check, ChevronDown, Loader2 } from 'lucide-react';
import { cn } from '../../lib/utils';

/**
 * Minimal brand shape required by BrandSwitcher.
 * Compatible with @bill/core-types `UserBrandInfo`, but kept local so the
 * UI library doesn't depend on backend types.
 */
export interface BrandSwitcherBrand {
  id: string;
  name: string;
  role: string;
  type: string;
  parentId: string | null;
}

export interface BrandSwitcherCompany<TBrand extends BrandSwitcherBrand = BrandSwitcherBrand> {
  id: string;
  name: string;
  brands: TBrand[];
}

export interface BrandSwitcherProps<TBrand extends BrandSwitcherBrand = BrandSwitcherBrand> {
  companies: BrandSwitcherCompany<TBrand>[];
  currentBrand: TBrand;
  onSwitch: (brandId: string) => Promise<void>;
  isLoading?: boolean;
  /**
   * Optional filter — return false to hide a brand from the dropdown.
   * Useful for products that only target a subset of brand types
   * (e.g. BStaff only shows POINT_OF_SALE brands, not GROUP/CENTRAL).
   */
  filterBrand?: (brand: TBrand) => boolean;
}

type HierarchicalBrand<T extends BrandSwitcherBrand> = T & { isChild: boolean };

function organizeBrandsHierarchically<T extends BrandSwitcherBrand>(
  brands: T[]
): HierarchicalBrand<T>[] {
  const result: HierarchicalBrand<T>[] = [];
  const rootBrands = brands.filter((b) => b.parentId === null);

  for (const root of rootBrands) {
    result.push({ ...root, isChild: false });
    const children = brands.filter((b) => b.parentId === root.id);
    for (const child of children) {
      result.push({ ...child, isChild: true });
    }
  }

  const addedIds = new Set(result.map((b) => b.id));
  for (const brand of brands) {
    if (!addedIds.has(brand.id)) {
      result.push({ ...brand, isChild: false });
    }
  }

  return result;
}

function getBrandIcon(brand: BrandSwitcherBrand) {
  switch (brand.type) {
    case 'POINT_OF_SALE':
      return <MapPin className="h-4 w-4" />;
    case 'CENTRAL':
      return <Warehouse className="h-4 w-4" />;
    case 'GROUP':
    default:
      return <Building2 className="h-4 w-4" />;
  }
}

function formatRole(role: string) {
  const roleLabels: Record<string, string> = {
    owner: 'Owner',
    admin: 'Admin',
    manager: 'Manager',
    user: 'User',
  };
  return roleLabels[role.toLowerCase()] || role;
}

export function BrandSwitcher<TBrand extends BrandSwitcherBrand>({
  companies,
  currentBrand,
  onSwitch,
  isLoading = false,
  filterBrand,
}: BrandSwitcherProps<TBrand>) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Apply filterBrand to each company's brands
  const filteredCompanies = useMemo(() => {
    if (!filterBrand) return companies;
    return companies
      .map((company) => ({ ...company, brands: company.brands.filter(filterBrand) }))
      .filter((company) => company.brands.length > 0);
  }, [companies, filterBrand]);

  const showCompanyGroups = filteredCompanies.length > 1;

  const flatBrands = useMemo((): HierarchicalBrand<TBrand>[] => {
    if (showCompanyGroups || filteredCompanies.length === 0) return [];
    return organizeBrandsHierarchically(filteredCompanies[0].brands);
  }, [filteredCompanies, showCompanyGroups]);

  const groupedCompanies = useMemo(() => {
    if (!showCompanyGroups) return [];
    return filteredCompanies.map((company) => ({
      ...company,
      brands: organizeBrandsHierarchically(company.brands),
    }));
  }, [filteredCompanies, showCompanyGroups]);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const handleSwitch = async (brandId: string) => {
    if (brandId === currentBrand.id || isLoading) return;
    try {
      await onSwitch(brandId);
      setIsOpen(false);
    } catch {
      /* error handled by caller */
    }
  };

  // Hide switcher entirely if no brand available after filter
  const hasBrands = filteredCompanies.some((c) => c.brands.length > 0);
  if (!hasBrands) return null;

  // Check if currentBrand is in the filtered list (it might not be, e.g. user is on a GROUP brand)
  const currentBrandVisible = filteredCompanies.some((c) =>
    c.brands.some((b) => b.id === currentBrand.id)
  );

  const renderBrandItem = (brand: HierarchicalBrand<TBrand>, extraIndent = false) => {
    const isActive = brand.id === currentBrand.id;
    return (
      <button
        key={brand.id}
        type="button"
        onClick={() => handleSwitch(brand.id)}
        disabled={isActive}
        className={cn(
          'flex w-full items-center justify-between py-2.5 text-left transition-colors',
          extraIndent ? 'px-3 pl-8' : brand.isChild ? 'px-3 pl-6' : 'px-3',
          isActive ? 'bg-primary-50 cursor-default' : 'hover:bg-neutral-100 cursor-pointer'
        )}
        role="option"
        aria-selected={isActive}
      >
        <div className="flex items-center gap-2.5 min-w-0">
          <span className={cn('flex-shrink-0', isActive ? 'text-primary-500' : 'text-dark-400')}>
            {getBrandIcon(brand)}
          </span>
          <div className="min-w-0">
            <p
              className={cn(
                'text-sm font-medium truncate',
                isActive ? 'text-primary-600' : 'text-dark-500'
              )}
            >
              {brand.name}
            </p>
            <p className={cn('text-xs', isActive ? 'text-primary-500' : 'text-neutral-500')}>
              {formatRole(brand.role)}
            </p>
          </div>
        </div>
        {isActive && <Check className="h-4 w-4 text-primary-500 flex-shrink-0" />}
      </button>
    );
  };

  return (
    <div ref={dropdownRef} className="relative px-3 py-3 border-b border-sidebar-border">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        disabled={isLoading}
        className={cn(
          'flex w-full items-center justify-between rounded-lg px-3 py-2.5',
          'bg-white hover:bg-neutral-100 transition-colors',
          'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-sidebar-bg',
          isLoading && 'opacity-70 cursor-not-allowed'
        )}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label="Changer d'établissement"
      >
        <div className="flex items-center gap-2.5 min-w-0">
          <span className="text-primary-500 flex-shrink-0">
            {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : getBrandIcon(currentBrand)}
          </span>
          <div className="text-left min-w-0">
            <p className="text-sm font-medium text-dark-500 truncate">
              {currentBrandVisible ? currentBrand.name : 'Selectionner un site'}
            </p>
            <p className="text-xs text-primary-500">
              {currentBrandVisible ? formatRole(currentBrand.role) : '\u00a0'}
            </p>
          </div>
        </div>
        <ChevronDown
          className={cn(
            'h-4 w-4 text-dark-400 transition-transform flex-shrink-0',
            isOpen && 'rotate-180'
          )}
        />
      </button>

      {isOpen && !isLoading && (
        <div
          className={cn(
            'absolute left-3 right-3 z-50 mt-2',
            'rounded-lg border border-neutral-200 bg-white py-1 shadow-lg',
            'animate-in fade-in-0 zoom-in-95 duration-100',
            'max-h-80 overflow-y-auto'
          )}
          role="listbox"
          aria-label="Liste des établissements"
        >
          {showCompanyGroups
            ? groupedCompanies.map((company) => (
                <div key={company.id}>
                  <div className="px-3 py-2 text-xs font-semibold text-neutral-500 uppercase tracking-wider bg-neutral-50 border-b border-neutral-100">
                    {company.name}
                  </div>
                  {company.brands.map((brand) =>
                    renderBrandItem(brand as HierarchicalBrand<TBrand>, brand.isChild)
                  )}
                </div>
              ))
            : flatBrands.map((brand) => renderBrandItem(brand))}
        </div>
      )}
    </div>
  );
}
